import { useMemo, useState, useEffect } from "react";
import { Modal, Select, Input, Button, Alert, Spin, message } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_QR_ASSIGNMENT, GET_INVENTORY_BY_PLACE } from "../graphql/queries";
import { Inventory } from "../types";

interface QrAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  qrId: string;
  placeId: string;
  placeName: string;
  eventId: string;
}

export default function QrAssignmentModal({
  open,
  onClose,
  qrId,
  placeId,
  placeName,
  eventId,
}: QrAssignmentModalProps) {
  const userId = localStorage.getItem("userId") || "";
  const [inventoryId, setInventoryId] = useState<string | undefined>(undefined);
  const [note, setNote] = useState("");

  const { data, loading: loadingInv } = useQuery<{
    getInventoryByPlace: { inventory: Inventory[]; success: boolean };
  }>(GET_INVENTORY_BY_PLACE, {
    variables: { input: { userId, eventId, placeId } },
    skip: !open || !userId || !eventId || !placeId,
  });

  const inventoryOptions = useMemo(() => {
    const raw = data?.getInventoryByPlace?.inventory || [];
    const seen = new Map<string, Inventory>();
    for (const inv of raw) {
      if (inv?.id && !seen.has(inv.id)) seen.set(inv.id, inv);
    }
    return Array.from(seen.values()).map((inv) => ({
      label: inv.name,
      value: inv.id,
    }));
  }, [data?.getInventoryByPlace?.inventory]);

  const [createQrAssignment, { loading: submitting }] = useMutation<{
    createQrAssignment: { success?: boolean; message?: string; id?: string };
  }>(CREATE_QR_ASSIGNMENT);

  useEffect(() => {
    if (!open) {
      setInventoryId(undefined);
      setNote("");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!userId || !eventId) {
      return;
    }
    try {
      const { data: res } = await createQrAssignment({
        variables: {
          input: {
            userId,
            qrId,
            eventId,
            placeId,
            inventoryId: inventoryId ?? null,
            note: note.trim() || null,
          },
        },
      });
      if (res?.createQrAssignment?.success) {
        message.success("QR byl přiřazen.");
        onClose();
      } else {
        message.error(res?.createQrAssignment?.message || "Chyba při ukládání.");
      }
    } catch (e) {
      console.error(e);
      message.error("Chyba při ukládání.");
    }
  };

  const canSubmit = Boolean(userId && eventId && placeId && qrId);

  return (
    <Modal
      title="Nastavit QR"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Zrušit
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={submitting}
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          Uložit
        </Button>,
      ]}
      destroyOnClose
      centered
    >
      {!eventId ? (
        <Alert type="warning" showIcon message="Nejprve vyberte akci v menu." style={{ marginBottom: 12 }} />
      ) : null}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--ant-color-text-secondary, #888)", marginBottom: 4 }}>ID QR</div>
        <div style={{ fontWeight: 600, wordBreak: "break-all" }}>{qrId}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "var(--ant-color-text-secondary, #888)", marginBottom: 4 }}>Místo</div>
        <div style={{ fontWeight: 500 }}>{placeName}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>Inventář</div>
        {loadingInv ? (
          <Spin />
        ) : (
          <Select
            allowClear
            placeholder="Vyberte inventář (nepovinné)"
            style={{ width: "100%" }}
            options={inventoryOptions}
            value={inventoryId}
            onChange={(v) => setInventoryId(v)}
          />
        )}
      </div>
      <div>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>Poznámka</div>
        <Input.TextArea
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Volitelná poznámka…"
        />
      </div>
    </Modal>
  );
}
